import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from '@mantine/hooks';
import { Box, useMantineTheme } from '@mantine/core';

import { useStore } from '../../state';
import { StoreController } from './StoreController';
import { CartController } from '../cart/CartController';
import { useStoreHandlers } from './hooks/useStoreHandlers';
import { useActiveCategoryTracking } from './hooks/useActiveCategoryTracking';

import { Footer } from '../../components/layout/Footer';
import { MobileHeader } from '../../components/layout/MobileHeader';
import { TopNavigation } from '../../components/navigation/TopNavigation';
import { HeroBackdrop } from '../../components/background/HeroBackdrop';
import { Sidebar } from '../../components/sidebar/Sidebar';
import { CategoryContent } from '../../components/category/CategoryContent';
import { CategorySkeleton } from '../../components/loader/CategorySkeleton';
import { CartView } from '../cart/CartView';
import { MobileCartWrapper } from '../cart/MobileCartWrapper';

import { LAYOUT_CONSTANTS } from '../../components/layout/LayoutConstants';

import './StoreView.scss';

export const StoreView: React.FC = observer(() => {
  const rootStore = useStore();
  const { storeStore: store, cartStore } = rootStore;

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const controller = useMemo(() => new StoreController(rootStore), [rootStore]);
  const cartController = useMemo(() => new CartController(rootStore), [rootStore]);

  const activeCollection = useActiveCategoryTracking(store.collections);

  const handlers = useStoreHandlers(rootStore, cartController);

  useEffect(() => {
    const init = async () => {
      await controller.load();
    };
    init();
    return () => store.resetAll();
  }, [controller, store]);

  const containerStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 2
  };

  const contentWrapperStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    paddingTop: isMobile ? LAYOUT_CONSTANTS.MOBILE_HEADER_HEIGHT : 0,
    width: '100%',
    boxSizing: 'border-box'
  };

  const mainContentStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    boxSizing: 'border-box',
    overflow: isMobile ? 'hidden' : 'visible'
  };

  const innerContentStyle: React.CSSProperties = {
    width: '100%',
    padding: isMobile ? '16px' : 32,
    paddingBottom: isMobile ? LAYOUT_CONSTANTS.MOBILE_CART_HEIGHT + 16 : 32,
    maxWidth: isMobile ? '100%' : '1075px',
    boxSizing: 'border-box'
  };

  return (
    <>
      <HeroBackdrop imageUrl='/assets/images/hero.png' />

      {isMobile ? (
        <MobileHeader isOpen={rootStore.applicationStore.isSidebarOpen} onToggle={handlers.handleToggleSidebar} />
      ) : (
        <TopNavigation transparent />
      )}

      <Box style={containerStyle}>
        <Box style={contentWrapperStyle}>
          <Sidebar
            collections={store.collections}
            activeCollection={activeCollection}
            onCollectionClick={handlers.handleCollectionClick}
            playersOnline={store.playersOnline}
            discordOnline={store.discordOnline}
            isMobile={isMobile}
            isOpen={rootStore.applicationStore.isSidebarOpen}
            onClose={handlers.handleCloseSidebar}
            isLoading={store.isLoading}
          />

          <Box style={mainContentStyle}>
            <Box style={innerContentStyle}>
              {store.isLoading ? (
                <>
                  {[1, 2, 3].map((index) => (
                    <Box key={index} mb={32}>
                      <CategorySkeleton packagesCount={6} />
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {store.collections.map((collection) => (
                    <Box key={collection.id} id={`collection-${collection.id}`} mb={32}>
                      <CategoryContent
                        collection={collection}
                        onAddToCart={handlers.handleAddToCart}
                        onViewDetails={handlers.handleViewDetails}
                        cartItems={cartStore.items}
                        onUpdateQuantity={handlers.handleUpdateQuantity}
                        onRemoveFromCart={handlers.handleRemoveFromCart}
                        isLoading={cartStore.isLoading}
                        loadingLineId={cartStore.loadingLineId}
                        loadingOperation={cartStore.loadingOperation}
                        productBanners={store.productBanners}
                      />
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box style={{ marginBottom: isMobile ? LAYOUT_CONSTANTS.MOBILE_CART_HEIGHT : 0 }}>
          <Footer />
        </Box>
      </Box>

      {isMobile && (
        <MobileCartWrapper isMobile={isMobile}>
          <CartView isMobile={isMobile} />
        </MobileCartWrapper>
      )}
    </>
  );
});
